// Aus Gespraechsnotizen werden Vertragspassus - als eigene Spalte im Studio.
//
// Warum eine eigene Spalte und kein Block in der Seitenleiste: Ein Gespraech
// braucht Platz. In der schmalen Einstellungsspalte war der Verlauf nicht
// lesbar und das Eingabefeld zwei Zeilen hoch - damit liess sich nicht
// arbeiten. Jetzt steht der Chat links ueber die volle Hoehe des
// Arbeitsbereichs; der Verlauf scrollt darin, das Schreibfeld bleibt unten.
//
// Der Ablauf bleibt zweistufig: Die KI entwirft, der Mensch urteilt. Kein
// Passus wandert in den Vertrag, solange er nicht ausdruecklich auf "passt"
// steht, und keine Aenderung an bestehendem Text ohne eigene Zustimmung.
//
// ZUR BENENNUNG: In der Oberflaeche heissen die beiden Dinge "Ergaenzungen" und
// "Ersetzungen" - das benennt die Richtung und war fuer Bearbeiter verstaendlich,
// waehrend "Passus" und "Aenderung" beide nach "etwas am Vertrag machen" klangen.
// Im Code bleiben die alten Namen (passus, aenderungen): Sie stecken in
// Dateinamen, API-Operationen und in bereits gespeicherten Sitzungen. Ein
// Umbenennen dort braeuchte eine Migration und brachte nichts.
//
// Die freigegebenen Passus gehen als Ad-hoc-Klauseln in den Erzeugungs-Payload
// (onKlauselnChange). Sie landen NICHT in der Paragraphen-Bibliothek - sie
// gehoeren zu diesem einen Vorgang.
//
// Gestaltung seit dem 23.09.2026 wie das Abwaegen: Wegweiser statt
// Erklaertext, solange nichts da ist; was eine Runde an den Listen getan hat,
// steht als Zeile im Gespraech (mit "Ansehen") statt als Toast; die Karten
// stehen in VerhandlungsKarten.jsx.
//
// Erst gliedern, dann entwerfen (seit 29.09.2026): Ein langes Schreiben -
// etwa das Anwaltsschreiben der Eigentuemerseite - zerlegt die KI zuerst in
// Punkte (Reiter "Gliederung", GliederungsListe.jsx). ENPOWER hakt an, und nur
// das Angehakte wird entworfen, fuenf Punkte je Runde. In einem Zug war es
// doppelt, unvollstaendig und schrieb Forderungen der Gegenseite so in den
// Vertrag, als seien sie vereinbart.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { T, knopfStil, primaerKnopfStil, feldStil, reiterStil } from '../../lib/designTokens';
import { Aufklapper, Wegweiser, textKnopfStil } from './abwaegenAnzeige';
import KiDenkt from './KiDenkt';
import { ErgaenzungKarte, ErsetzungKarte, ErsetzungenHinweis, VerworfeneKarte } from './VerhandlungsKarten';
import { Aufraeumen, Blase, FehlendHinweis, RundenErgebnis } from './gespraechAnzeige';
import GliederungsListe from './GliederungsListe';
import { parteienImText, textAusHtml } from '../../lib/parteien';
// Nutzlasten, Zaehler und Texte teilt sich dieses Panel seit dem 23.09.2026
// mit der mobilen Oberflaeche (src/lib/gespraech.js, src/lib/kiDatei.js).
import {
  ENTWERFEN_NACHRICHT, GESPRAECH_SCHRITTE, GESPRAECH_WEGWEISER, aenderungStandNutzlast, angleichenHinweis,
  aufraeumbarZahl, aufraeumenMeldung, chatNutzlast, ersetzungenWort, fehlendAus, folgeMeldung, gespraechAdresse,
  gespraechKoerper, gespraechPostAdresse, gespraechZaehler, nachreichenNachricht, rundenErgebnis,
  standNutzlast, ueberarbeitenNutzlast, zuErsetzungNutzlast,
  GLIEDERUNG_SCHRITTE, entwerfenKnopfText, entwurfsBilanz, entwurfsBilanzText, gliedernEmpfohlen, gliedernNutzlast,
  gliederungEntwerfenNutzlast, gliederungFortschritt, gliederungMitAenderungen, gliederungsPortionen,
  gliederungsPunkteNutzlast, gliederungsQuelleDa, gliederungZaehler, neuestesSchreiben,
  zurueckgesetztMeldung, zuruecksetzenNutzlast,
  ergaenzungVorschauHinweis, ersetzungVorschauHinweis, hinweisNachFreigabe, sucheErsetzungImVertrag,
} from '../../lib/gespraech';
import { DATEI_ANNAHME, dateiGelesenText, dateiNutzlast } from '../../lib/kiDatei';
import { ersetzungsOrt, punktImVertrag, schonImVertrag, vertragsBloecke } from '../../lib/imVertrag';
import {
  AlertTriangle, ArrowRight, ChevronLeft, ChevronRight, FileUp, ListChecks, MessageSquare, RotateCcw,
  Paperclip, Send, Sparkles,
} from 'lucide-react';

const ruhigerText = { fontSize: T.tText, color: T.muted, lineHeight: 1.6 };

// Die Gestaltungssprache steht in src/lib/designTokens.js - sie gilt fuer das
// ganze Werkzeug, nicht nur fuer diesen Bereich. Hier stehen nur die Stile,
// die es ausschliesslich in diesem Panel gibt.

// Die Breite stammt aus PANEL-SPEZIFIKATION.md des Lageplan-Tools. Fest 440 px
// waren zu schmal, um einen Gespraechsverlauf und daneben Vertragstext zu
// lesen; feste 720 px wuerden auf einem 1366er Bildschirm die Vorschau
// erdruecken. Das clamp loest beides.
const SPALTE_BREIT = 'clamp(460px, 46vw, 720px)';
const OFFEN_SPEICHER = 'enpower.verhandlung.offen.v1';
const OHNE_GLIEDERUNG_SPEICHER = 'enpower.verhandlung.ohneGliederung.v1.';

// Belegliste, Urteilsfarben und die Abschnittsmarke stehen seit dem 23.09.2026
// in abwaegenAnzeige.jsx - das Abwaegen ist jetzt ein eigener Bereich, und
// die Abschnittsmarke brauchen beide Seiten.

export default function VerhandlungsPassus({
  apiBase = '/api',
  authHeaders = {},
  dealId = '',
  contractKey = '',
  contractLabel = '',
  abschnitte = [],
  vertragsHtml = '',
  // Liefert den Vertrag frisch vor jedem KI-Aufruf (beim Bearbeiten: den
  // Stand im Editor). Ohne sie gilt vertragsHtml.
  holeVertragsHtml = null,
  isAuthenticated = false,
  onKlauselnChange = null,
  // Meldet die zugestimmten Ersetzungen - beim Erstellen wendet das Studio sie
  // beim Füllen und Abschließen auf beide Varianten an (seit 24.10.2026).
  onErsetzungenChange = null,
  // Nur beim Bearbeiten gesetzt: Dort setzt „… in den Vertrag übernehmen“ die
  // Ersetzungen in den Vertrag im Editor.
  onVertragGeaendert = null,
  // Fuegt die freigegebenen Ergaenzungen in den bearbeiteten Vertrag ein.
  // Nur beim Bearbeiten gesetzt - beim Erstellen erledigt das "Fuellen".
  onErgaenzungenEinfuegen = null,
  onSpringeZuStelle = null,
  // Wird ein Vertrag bearbeitet (hochgeladen oder "Bearbeiten" nach dem Fuellen)?
  // Dann erinnert ein Hinweis nach Zustimmen/Freigeben an den Knopf, der es einsetzt.
  bearbeitet = false,
  // Beim Erstellen: Freigegebenes und Zugestimmtes kommt von selbst mit Füllen
  // und Abschließen hinein - die Hinweise sagen das statt auf einen Knopf zu zeigen.
  erstellen = false,
}) {
  const [sitzung, setSitzung] = useState(null);
  const [kiBereit, setKiBereit] = useState(true);
  const [laeuft, setLaeuft] = useState('');
  // Was die letzte Runde bewirkt hat: { teile, ziel, nichtVerwendet }.
  const [letzteRunde, setLetzteRunde] = useState(null);
  const [verworfenOffen, setVerworfenOffen] = useState(false);
  const [nachricht, setNachricht] = useState('');
  // Die eigene Nachricht, solange die KI antwortet - steht sofort im
  // Gespraech. Vorher erschien sie erst mit der Antwort (Nutzer, 23.09.2026:
  // "meine Nachricht sehe ich immer erst nach 5 Sekunden").
  const [unterwegs, setUnterwegs] = useState('');
  // Bei "1 zu 1": nummerierte Absaetze, die in keinem Passus stehen.
  const [fehlend, setFehlend] = useState([]);
  const [reiter, setReiter] = useState('gespraech');
  // Waehrend "Ausgewaehlte entwerfen": { fertig, gesamt, portion }.
  const [entwurfsLauf, setEntwurfsLauf] = useState(null);
  const anhalten = useRef(false);
  // Haekchen und Vorgaben der Gliederung werden nacheinander gespeichert -
  // gleichzeitig laesen zwei Anfragen denselben Stand, und die zweite
  // verwuerfe die erste.
  const wahlKette = useRef(Promise.resolve());
  // Die Zeit des Schreibens, bei dem der Bearbeiter "Ohne Gliederung weiter"
  // gewaehlt hat - je Deal im Browser gemerkt, sonst stuende der Hinweis nach
  // jedem Neuladen wieder da (etwa bei "1 zu 1").
  const [ohneGliederung, setOhneGliederung] = useState('');
  // Die Rueckfrage zu "Neu beginnen" ist offen.
  const [neuBeginnenFrage, setNeuBeginnenFrage] = useState(false);
  const [offen, setOffen] = useState(() => {
    try { return localStorage.getItem(OFFEN_SPEICHER) !== 'zu'; } catch (_) { return true; }
  });

  const dateiFeld = useRef(null);
  const verlaufEnde = useRef(null);

  // Ohne Ref liefe der Callback bei jeder Neuzeichnung erneut und setzte den
  // Zustand der Elternkomponente, was wieder eine Neuzeichnung ausloeste.
  const meldeKlauseln = useRef(onKlauselnChange);
  meldeKlauseln.current = onKlauselnChange;
  const meldeErsetzungen = useRef(onErsetzungenChange);
  meldeErsetzungen.current = onErsetzungenChange;

  // Der Vertrag, wie er JETZT ist. Beim Bearbeiten aendert der Bearbeiter
  // ihn im Editor von Hand; die KI soll diese Fassung sehen, nicht die vom
  // Hochladen (seit 29.09.2026).
  const aktuellesHtml = useCallback(() => {
    if (holeVertragsHtml) {
      try {
        const html = holeVertragsHtml();
        if (typeof html === 'string' && html) return html;
      } catch (_) { /* dann gilt der zuletzt bekannte Stand */ }
    }
    return vertragsHtml;
  }, [holeVertragsHtml, vertragsHtml]);

  // Die Spalte fuellt den Arbeitsbereich - ohne JavaScript.
  //
  // Bis zum 22.09.2026 stand hier position:sticky mit height:100vh und ein
  // Effekt, der bei jedem Scrollschritt getBoundingClientRect() las und
  // danach style.height setzte. Beides zusammen erzwingt pro Frame zwei
  // Layout-Durchlaeufe, und genau das war als Ruckeln zu sehen.
  //
  // Der Grund fuer die Messung: Der Rahmen um das Studio (App.js) scrollte
  // selbst, 100vh war also die falsche Bezugsgroesse, und den Abstand nach
  // oben kannte CSS nicht. Jetzt behaelt der Rahmen seine Hoehe, der
  // Arbeitsbereich bekommt per flex:1 die Resthoehe, und diese Spalte nimmt
  // mit height:100% genau die Hoehe des Bereichs an. Das Schreibfeld sitzt
  // damit immer am unteren Rand, ohne dass etwas gerechnet wird.

  const umschalten = useCallback(() => {
    setOffen((v) => {
      const neu = !v;
      try { localStorage.setItem(OFFEN_SPEICHER, neu ? 'auf' : 'zu'); } catch (_) { /* egal */ }
      return neu;
    });
  }, []);

  const uebernimm = useCallback((daten) => {
    if (daten?.sitzung) setSitzung(daten.sitzung);
    if (typeof daten?.kiBereit === 'boolean') setKiBereit(daten.kiBereit);
    if (Array.isArray(daten?.klauseln) && meldeKlauseln.current) {
      meldeKlauseln.current(daten.klauseln);
    }
    if (daten?.sitzung && meldeErsetzungen.current) {
      meldeErsetzungen.current((daten.sitzung.aenderungen || []).filter((a) => a?.stand === 'zugestimmt'));
    }
  }, []);

  const melde = useCallback((fehler, ersatz) => {
    const detail = fehler?.response?.data?.detail || fehler?.message || ersatz;
    const code = fehler?.response?.data?.code || '';
    // Der fehlende Schluessel ist kein Fehler des Bearbeiters - er soll wissen,
    // an wen er sich wendet, statt es erneut zu versuchen.
    if (code === 'KI_KEIN_SCHLUESSEL') {
      setKiBereit(false);
      toast.error('Für die KI ist kein Vibecode-Schlüssel hinterlegt. Ein Administrator muss ihn eintragen.');
      return;
    }
    toast.error(detail || ersatz);
  }, []);

  useEffect(() => {
    // Erst leeren, dann laden. Ohne das blieben beim Wechsel des Vorgangs die
    // freigegebenen Passus des vorigen stehen - und landeten im falschen
    // Vertrag, solange die neue Sitzung noch unterwegs ist.
    setSitzung(null);
    setLetzteRunde(null);
    setVerworfenOffen(false);
    setUnterwegs('');
    setFehlend([]);
    setEntwurfsLauf(null);
    setNeuBeginnenFrage(false);
    anhalten.current = true;
    try { setOhneGliederung(localStorage.getItem(`${OHNE_GLIEDERUNG_SPEICHER}${dealId}`) || ''); } catch (_) { setOhneGliederung(''); }
    if (meldeKlauseln.current) meldeKlauseln.current([]);
    if (meldeErsetzungen.current) meldeErsetzungen.current([]);

    if (!dealId || !isAuthenticated) return undefined;

    let abgebrochen = false;
    (async () => {
      try {
        const antwort = await axios.get(gespraechAdresse(apiBase, dealId), { headers: authHeaders });
        if (!abgebrochen) uebernimm(antwort.data);
      } catch (fehler) {
        if (!abgebrochen) melde(fehler, 'Die Verhandlung konnte nicht geladen werden.');
      }
    })();
    return () => { abgebrochen = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, dealId, isAuthenticated]);

  const sende = useCallback(async (nutzlast, kennung) => {
    if (!dealId) {
      toast.error('Ohne Vorgang lässt sich nichts speichern.');
      return null;
    }
    setLaeuft(kennung);
    try {
      const antwort = await axios.post(
        gespraechPostAdresse(apiBase),
        gespraechKoerper(dealId, contractKey, nutzlast),
        { headers: authHeaders }
      );
      uebernimm(antwort.data);
      return antwort.data;
    } catch (fehler) {
      melde(fehler, 'Die Aktion ist fehlgeschlagen.');
      return null;
    } finally {
      setLaeuft('');
    }
  }, [apiBase, authHeaders, contractKey, dealId, melde, uebernimm]);

  const verlauf = useMemo(() => sitzung?.verlauf || [], [sitzung]);
  const passusListe = useMemo(() => sitzung?.passus || [], [sitzung]);
  // Verworfenes steht gesammelt und eingeklappt am Ende - Nummern und
  // Reihenfolge gelten nur fuer den Rest.
  const aktive = useMemo(() => passusListe.filter((p) => p.stand !== 'verworfen'), [passusListe]);
  const verworfene = useMemo(() => passusListe.filter((p) => p.stand === 'verworfen'), [passusListe]);
  const aenderungen = useMemo(() => sitzung?.aenderungen || [], [sitzung]);
  // Wie die Parteien in diesem Vertrag heissen - fuer den Hinweis an Passus,
  // die andere Bezeichnungen benutzen (die KI gleicht nicht immer an).
  const parteien = useMemo(() => parteienImText(textAusHtml(vertragsHtml)), [vertragsHtml]);
  const zugestimmt = useMemo(() => aenderungen.filter((a) => a.stand === 'zugestimmt'), [aenderungen]);
  // Zu welcher Ergänzung eine Ersetzung gehört - mit der Nummer, unter der
  // sie im Reiter Ergänzungen steht (Verworfenes hat keine).
  const ergaenzungNach = useMemo(() => {
    const karte = new Map(passusListe.map((p) => [p.id, { titel: p.titel || '', nummer: null }]));
    aktive.forEach((p, i) => { karte.set(p.id, { titel: p.titel || '', nummer: i + 1 }); });
    return karte;
  }, [aktive, passusListe]);

  // Was "Entwuerfe aufraeumen" entfernen wuerde: alles ausser Freigegebenem.
  const aufraeumbar = aufraeumbarZahl(passusListe, aenderungen);

  const zaehler = useMemo(() => gespraechZaehler(passusListe), [passusListe]);

  const gliederung = sitzung?.gliederung || null;
  const gliederungsStand = useMemo(() => gliederungZaehler(gliederung), [gliederung]);
  const quelleDa = useMemo(() => gliederungsQuelleDa(verlauf), [verlauf]);
  // Die Liste sperrt auch zwischen zwei Runden des Entwerfens.
  const gliederungGesperrt = Boolean(laeuft) || Boolean(entwurfsLauf);
  // Steht eine Ergaenzung schon im Vertrag? Ohne KI, am Vertrag, wie er
  // gerade ist (src/lib/imVertrag.js, seit 29.09.2026) - beim Bearbeiten also
  // am hochgeladenen Vertrag samt Handaenderungen. Eingefuegte Ergaenzungen
  // zaehlen nicht gegen sich selbst.
  const vertragsAbsaetze = useMemo(() => vertragsBloecke(vertragsHtml), [vertragsHtml]);
  // Wo jede Ersetzung greift - für die Marke oben in der Karte (seit 24.10.2026).
  const ersetzungsOrtNach = useMemo(
    () => new Map(aenderungen.map((a) => [a.id, ersetzungsOrt(a, vertragsAbsaetze)])),
    [aenderungen, vertragsAbsaetze],
  );
  const imVertragNach = useMemo(
    () => new Map(aktive.map((p) => [p.id, schonImVertrag(p, vertragsAbsaetze)])),
    [aktive, vertragsAbsaetze],
  );
  const doppeltZahl = aktive.filter((p) => imVertragNach.get(p.id)).length;
  const doppeltFreigegeben = aktive.filter((p) => p.stand === 'passt' && imVertragNach.get(p.id)).length;
  // Dasselbe fuer die Punkte der Gliederung (seit 29.09.2026, Wunsch des
  // Nutzers): Was schon im Vertrag steht, bekommt einen Hinweis, und "Alle
  // waehlen" laesst es aus - sonst entwirft die KI es ein zweites Mal.
  const gliederungImVertrag = useMemo(
    () => new Map((gliederung?.punkte || []).map((p) => [p.id, punktImVertrag(p, vertragsAbsaetze)])),
    [gliederung, vertragsAbsaetze],
  );
  // "Neu beginnen" gibt es erst, wenn es etwas zu leeren gibt.
  const etwasDa = verlauf.length > 0 || passusListe.length > 0 || aenderungen.length > 0 || Boolean(gliederung);

  // Nach jedem neuen Beitrag ans Ende springen - sonst muesste man nach jeder
  // Antwort von Hand scrollen.
  useEffect(() => {
    if (reiter !== 'gespraech') return;
    verlaufEnde.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [verlauf.length, reiter, laeuft, fehlend.length, letzteRunde]);

  // Eine Runde im Gespraech. Die eigene Nachricht steht sofort da; danach
  // meldet die Seite, was sich an der Liste getan hat, und ob bei "1 zu 1"
  // Absaetze fehlen.
  const runde = useCallback(async (frage, { erstGliedern = false } = {}) => {
    setUnterwegs(frage);
    setFehlend([]);
    setLetzteRunde(null);
    const aenderungenVorher = aenderungen;
    const daten = await sende(chatNutzlast({
      nachricht: frage,
      vertragsart: contractLabel || contractKey,
      paragraphen: abschnitte,
      vertragsHtml: aktuellesHtml(),
      erstGliedern,
    }), 'chat');
    setUnterwegs('');
    if (!daten) return null;
    // Ein langes Schreiben hat der Server erst gegliedert (seit 29.09.2026).
    if (daten.gegliedert) {
      setReiter('gliederung');
      toast.success(`${daten.gegliedert} Punkte gegliedert — hake an, was ENPOWER übernimmt.`);
      return daten;
    }
    setFehlend(fehlendAus(daten));
    setLetzteRunde(rundenErgebnis(daten, aenderungenVorher));
    return daten;
  }, [abschnitte, aenderungen, aktuellesHtml, contractKey, contractLabel, sende]);

  const schicken = useCallback(async () => {
    const frage = String(nachricht || '').trim();
    if (!frage) return;
    setNachricht('');
    // Seit 09.10.2026 gliedert die Seite NICHT mehr von selbst (Nutzer: „Es wurde
    // zwanghaft gegliedert“ - trotz genauer Angaben zu Stelle und Wortlaut).
    // Gegliedert wird nur noch auf Knopfdruck („Erst gliedern“ im Hinweis).
    const daten = await runde(frage);
    // Bleibt die Nachricht unbeantwortet, soll sie nicht verloren sein.
    if (!daten) setNachricht(frage);
  }, [nachricht, runde]);

  const fehlendeNachreichen = useCallback(() => {
    runde(nachreichenNachricht(fehlend));
  }, [fehlend, runde]);

  // Stand einer Ergänzung setzen oder sie löschen - und sagen, was mit ihren
  // Ersetzungen geschehen ist (seit 29.09.2026 folgen sie ihr).
  const setzePassusStand = useCallback(async (passusId, stand, hinweis) => {
    const daten = await sende(standNutzlast(passusId, stand, hinweis), `passus-${passusId}`);
    const meldung = folgeMeldung(daten);
    if (meldung) toast.info(meldung);
    const merke = daten ? hinweisNachFreigabe({
      art: 'ergaenzung', stand, bearbeitet: bearbeitet && Boolean(onErgaenzungenEinfuegen), erstellen,
    }) : null;
    if (merke) toast.info(merke);
    return daten;
  }, [bearbeitet, erstellen, onErgaenzungenEinfuegen, sende]);

  const setzeAenderungStand = useCallback(async (aenderungId, stand) => {
    const daten = await sende(aenderungStandNutzlast(aenderungId, stand, aktuellesHtml()), `aenderung-${aenderungId}`);
    // Stand die Stelle nur fast wortgleich im Vertrag, hat der Server das Zitat
    // durch den Vertragstext ersetzt und NICHT zugestimmt (seit 09.10.2026).
    if (daten?.angeglichen) {
      toast.warning(daten.angeglichen, { duration: 12000 });
      return daten;
    }
    const merke = daten ? hinweisNachFreigabe({
      art: 'ersetzung', stand, bearbeitet: bearbeitet && Boolean(onVertragGeaendert), erstellen,
    }) : null;
    if (merke) toast.info(merke);
    return daten;
  }, [aktuellesHtml, bearbeitet, erstellen, onVertragGeaendert, sende]);

  // "In der Vorschau" bei einer Ersetzung: erst der neue, dann der alte Wortlaut,
  // und eine Meldung, die sagt, was noch fehlt (sucheErsetzungImVertrag).
  const springeZuErsetzung = useCallback((aenderung) => {
    if (!onSpringeZuStelle) return;
    let ohneVorschau = false;
    const gefunden = sucheErsetzungImVertrag(aenderung, (text) => {
      if (ohneVorschau) return false;
      const ergebnis = onSpringeZuStelle('', text, { still: true });
      if (ergebnis === null) ohneVorschau = true;
      return ergebnis === true;
    });
    if (ohneVorschau) return;
    const meldung = ersetzungVorschauHinweis(aenderung, gefunden, { erstellen });
    if (meldung) toast[meldung.ton](meldung.text);
  }, [erstellen, onSpringeZuStelle]);

  const springeZuErgaenzung = useCallback((passus) => {
    if (!onSpringeZuStelle) return;
    const ergebnis = onSpringeZuStelle(`verhandlung-${passus.id}`, passus.absaetze?.[0]?.text || '', { still: true });
    if (ergebnis === false) {
      toast.info(ergaenzungVorschauHinweis({ stand: passus.stand, einfuegenMoeglich: Boolean(onErgaenzungenEinfuegen) }));
    }
  }, [onErgaenzungenEinfuegen, onSpringeZuStelle]);

  // Eine Lücke „[Frist Baubeginn]“ selbst füllen (seit 24.10.2026) - nur die
  // Lücke, der Stand bleibt (op "luecke-fuellen", api/_lib/luecken.js).
  const fuelleLuecke = useCallback(async (art, id, luecke, wert) => {
    const daten = await sende({ op: 'luecke-fuellen', art, id, luecke, wert }, `${art === 'aenderung' ? 'aenderung' : 'passus'}-${id}`);
    if (daten) toast.success(`Eingesetzt: ${luecke} → „${wert}“`);
    return daten;
  }, [sende]);

  const loeschePassus = useCallback(async (passusId) => {
    const daten = await sende({ op: 'loeschen', passusId }, `passus-${passusId}`);
    const meldung = folgeMeldung(daten);
    if (meldung) toast.info(meldung);
    return daten;
  }, [sende]);

  const aufraeumen = useCallback(async () => {
    const daten = await sende({ op: 'entwuerfe-aufraeumen' }, 'aufraeumen');
    if (daten) toast.success(aufraeumenMeldung(daten.entfernt));
  }, [sende]);

  // "Neu beginnen" (seit 29.09.2026, Wunsch des Nutzers). Der Server sichert
  // vorher; "Rueckgaengig" am Hinweis holt den alten Stand einmal zurueck.
  const zurueckholen = useCallback(async () => {
    const daten = await sende({ op: 'gespraech-zurueckholen' }, 'zurueckholen');
    if (daten) toast.success('Der vorige Stand ist wieder da.');
  }, [sende]);

  const zuruecksetzen = useCallback(async (alles) => {
    setNeuBeginnenFrage(false);
    const daten = await sende(zuruecksetzenNutzlast(alles), 'zuruecksetzen');
    if (!daten) return;
    setLetzteRunde(null);
    setFehlend([]);
    setUnterwegs('');
    setReiter('gespraech');
    toast.success(zurueckgesetztMeldung(alles), {
      duration: 15000,
      action: { label: 'Rückgängig', onClick: () => { zurueckholen(); } },
    });
  }, [sende, zurueckholen]);

  // ---------- Erst gliedern, dann entwerfen ----------

  const gliedern = useCallback(async () => {
    setReiter('gliederung');
    const daten = await sende(gliedernNutzlast({
      vertragsHtml: aktuellesHtml(),
      vertragsart: contractLabel || contractKey,
    }), 'gliedern');
    if (daten?.punkte) toast.success(`${daten.punkte} Punkte gegliedert — hake an, was ENPOWER übernimmt.`);
  }, [aktuellesHtml, contractKey, contractLabel, sende]);

  // Haekchen und Vorgaben stehen sofort da; gespeichert wird im Hintergrund,
  // nacheinander. Die Antwort ueberschreibt die Anzeige nicht - sonst
  // sprangen schnell gesetzte Haekchen kurz zurueck. Scheitert es, gilt
  // wieder der Stand vom Server.
  const setzeGliederungsPunkte = useCallback((aenderungenListe) => {
    if (!aenderungenListe.length) return wahlKette.current;
    setSitzung((s) => (s?.gliederung ? { ...s, gliederung: gliederungMitAenderungen(s.gliederung, aenderungenListe) } : s));
    wahlKette.current = wahlKette.current.then(async () => {
      try {
        await axios.post(
          gespraechPostAdresse(apiBase),
          gespraechKoerper(dealId, contractKey, gliederungsPunkteNutzlast(aenderungenListe)),
          { headers: authHeaders },
        );
      } catch (fehler) {
        melde(fehler, 'Die Auswahl konnte nicht gespeichert werden.');
        try {
          const antwort = await axios.get(gespraechAdresse(apiBase, dealId), { headers: authHeaders });
          uebernimm(antwort.data);
        } catch (_) { /* die Meldung steht schon da */ }
      }
    });
    return wahlKette.current;
  }, [apiBase, authHeaders, contractKey, dealId, melde, uebernimm]);

  // Fuer genau dieses Schreiben kein Hinweis mehr - etwa, wenn es "1 zu 1"
  // eingearbeitet werden soll. Ein neueres Schreiben bekommt ihn wieder.
  const ohneGliederungWeiter = useCallback(() => {
    const zeit = neuestesSchreiben(sitzung?.verlauf)?.zeit || '';
    setOhneGliederung(zeit);
    try { localStorage.setItem(`${OHNE_GLIEDERUNG_SPEICHER}${dealId}`, zeit); } catch (_) { /* egal */ }
  }, [dealId, sitzung]);

  const alleWaehlen = useCallback((gewaehlt) => {
    // Schon Entworfenes bleibt, wie es ist - "Keine" soll nichts vergessen machen.
    // "Alle" nimmt nicht, was schon im Vertrag steht; von Hand anhaken geht.
    setzeGliederungsPunkte((gliederung?.punkte || [])
      .filter((p) => !p.entworfen && p.gewaehlt !== gewaehlt)
      .filter((p) => !gewaehlt || !gliederungImVertrag.get(p.id))
      .map((p) => ({ id: p.id, gewaehlt })));
  }, [gliederung, gliederungImVertrag, setzeGliederungsPunkte]);

  const gliederungVerwerfen = useCallback(async () => {
    await wahlKette.current;
    const daten = await sende({ op: 'gliederung-verwerfen' }, 'gliederung-verwerfen');
    if (daten) setReiter('gespraech');
  }, [sende]);

  // Fuenf Punkte je Runde ueber das normale Gespraech. Den Auftrag schreibt der
  // Server aus den gespeicherten Punkten und setzt sie auf "entworfen" - eine
  // abgebrochene Reihe laesst sich deshalb einfach fortsetzen.
  const entwerfeGewaehlte = useCallback(async () => {
    await wahlKette.current;
    const portionen = gliederungsPortionen(gliederung?.punkte);
    if (!portionen.length) return;
    const gesamt = portionen.reduce((n, p) => n + p.length, 0);
    const vorher = sitzung;
    anhalten.current = false;
    setLetzteRunde(null);
    setFehlend([]);
    let fertig = 0;
    let zuletzt = null;
    let rueckfragen = 0;
    try {
      for (const portion of portionen) {
        if (anhalten.current) break;
        setEntwurfsLauf({ fertig, gesamt, portion: portion.length });
        const daten = await sende(gliederungEntwerfenNutzlast({
          punktIds: portion.map((p) => p.id),
          vertragsart: contractLabel || contractKey,
          paragraphen: abschnitte,
          vertragsHtml: aktuellesHtml(),
        }), 'chat');
        if (!daten) break;
        zuletzt = daten;
        fertig += portion.length;
        const verlaufNeu = daten.sitzung?.verlauf || [];
        if (verlaufNeu[verlaufNeu.length - 1]?.rueckfrage) rueckfragen += 1;
      }
    } finally {
      setEntwurfsLauf(null);
    }
    if (!zuletzt) return;
    const bilanz = entwurfsBilanz(vorher, zuletzt.sitzung);
    toast.success(entwurfsBilanzText({ punkte: fertig, ...bilanz }));
    if (fertig < gesamt) {
      toast.info(`Angehalten nach ${fertig} von ${gesamt} Punkten — der Rest bleibt gewählt und lässt sich fortsetzen.`);
    }
    if (rueckfragen) {
      toast.info('Die KI hat eine Rückfrage — siehe Gespräch.');
      setReiter('gespraech');
    } else if (bilanz.ergaenzungen || bilanz.ersetzungen) {
      setReiter(bilanz.ergaenzungen ? 'passus' : 'aenderungen');
    }
  }, [abschnitte, aktuellesHtml, contractKey, contractLabel, gliederung, sende, sitzung]);

  const dateiWaehlen = useCallback(async (ereignis) => {
    const datei = ereignis.target.files?.[0];
    // Zuruecksetzen, damit dieselbe Datei erneut gewaehlt werden kann.
    ereignis.target.value = '';
    if (!datei) return;

    const leser = new FileReader();
    leser.onerror = () => toast.error(`„${datei.name}" ließ sich nicht lesen.`);
    leser.onload = async () => {
      // Hier immer ins Gespraech. Kundenschreiben zum Abwaegen haengt man
      // seit dem 23.09.2026 im eigenen Bereich "Abwaegen" an.
      const daten = await sende(dateiNutzlast(datei, leser.result, 'gespraech'), 'datei');
      if (daten?.datei) toast.success(dateiGelesenText(daten.datei));
      // 29.09.-09.10.2026 wurde ein langes Schreiben hier sofort gegliedert.
      // Jetzt nur noch auf Knopfdruck: Wer zur Datei Stelle und Wortlaut
      // vorgibt, will keine Gliederung (Nutzer, 09.10.2026). Der Hinweis
      // „Erst gliedern“ bleibt stehen, bis gegliedert oder übersprungen ist.
    };
    leser.readAsDataURL(datei);
  }, [sende]);

  const entwerfen = useCallback(async () => {
    const daten = await runde(ENTWERFEN_NACHRICHT);
    if (!daten) return;
    if (daten.neuePassus > 0 || daten.bericht?.ueberarbeitet?.length) setReiter('passus');
    else toast.info('Die KI hat daraus keinen neuen regelungsbedürftigen Punkt abgeleitet.');
  }, [runde]);

  // Getauscht wird mit dem naechsten NICHT verworfenen Nachbarn: Verworfenes
  // steht eingeklappt am Ende, ein Tausch damit waere unsichtbar.
  const verschiebe = useCallback((id, richtung) => {
    const index = passusListe.findIndex((p) => p.id === id);
    let ziel = index + richtung;
    while (ziel >= 0 && ziel < passusListe.length && passusListe[ziel].stand === 'verworfen') ziel += richtung;
    if (index < 0 || ziel < 0 || ziel >= passusListe.length) return;
    const reihung = passusListe.map((p) => p.id);
    [reihung[index], reihung[ziel]] = [reihung[ziel], reihung[index]];
    sende({ op: 'reihenfolge', reihenfolge: reihung }, `sortieren-${id}`);
  }, [passusListe, sende]);

  const ueberarbeiten = useCallback((passusId, hinweis) => sende(ueberarbeitenNutzlast({
    passusId, hinweis,
    vertragsart: contractLabel || contractKey, paragraphen: abschnitte,
    vertragsHtml: aktuellesHtml(),
  }), `passus-${passusId}`), [abschnitte, aktuellesHtml, contractKey, contractLabel, sende]);

  const angleichen = useCallback((passusId) => {
    if (!parteien) return null;
    return ueberarbeiten(passusId, angleichenHinweis(parteien));
  }, [parteien, ueberarbeiten]);

  // Aus einer Ergaenzung eine Ersetzung machen. Der Knopf sitzt direkt an der
  // Warnung "Der Vertrag regelt das schon" - dort entsteht die Frage, und dort
  // gehoert die Antwort hin. Vorher musste man dafuer zurueck ins Gespraech.
  const zuErsetzung = useCallback(async (passusId) => {
    const daten = await sende(zuErsetzungNutzlast({
      passusId,
      vertragsart: contractLabel || contractKey,
      vertragsHtml: aktuellesHtml(),
    }), `passus-${passusId}`);
    if (!daten) return;
    setReiter('aenderungen');
    toast.success('Als Ersetzung angelegt — bitte zustimmen, damit sie greift.');
  }, [aktuellesHtml, contractKey, contractLabel, sende]);

  const uebernehmen = useCallback(async () => {
    if (!onVertragGeaendert) return;
    const daten = await sende({ op: 'aenderungen-anwenden', vertragsHtml: aktuellesHtml() }, 'anwenden');
    if (!daten) return;
    if (typeof daten.html === 'string' && daten.html) {
      // Wirft etwa, wenn der Editor das Ergebnis nicht annimmt. Ohne das catch
      // ginge es still verloren - keine Meldung, der Vertrag bliebe unveraendert.
      try {
        onVertragGeaendert(daten.html);
      } catch (fehler) {
        toast.error(`Übernehmen hat nicht geklappt: ${fehler?.message || 'unbekannter Fehler'}`);
        return;
      }
    }
    if (daten.fehlgeschlagen > 0) {
      const gruende = (daten.ergebnisse || []).filter((e) => !e.erfolg).map((e) => e.grund);
      toast.error(`${ersetzungenWort(daten.fehlgeschlagen)} nicht anwendbar: ${gruende.join(' ')}`);
    }
    // Was schon drinstand (etwa mit dem Füllen eingesetzt), ist kein neues
    // Übernehmen - das soll die Meldung nicht behaupten (seit 24.10.2026).
    const schonDrin = Number(daten.schonDrin) || 0;
    const neuUebernommen = (Number(daten.angewandt) || 0) - schonDrin;
    if (neuUebernommen > 0) {
      toast.success(`${ersetzungenWort(neuUebernommen)} in den Vertrag übernommen.`);
    }
    if (schonDrin > 0) {
      toast.info(`${schonDrin === 1 ? '1 Ersetzung stand' : `${schonDrin} Ersetzungen standen`} schon im Vertrag.`);
    }
    // Etwa nach dem Streichen ganzer Absaetze: Die Nummern der folgenden
    // Punkte ruecken nicht von selbst nach.
    const hinweise = [...new Set((daten.ergebnisse || []).filter((e) => e.erfolg && e.hinweis).map((e) => e.hinweis))];
    if (hinweise.length > 0) toast.info(hinweise.join(' '));
  }, [aktuellesHtml, onVertragGeaendert, sende]);

  const ergaenzungenEinfuegen = useCallback(async () => {
    if (!onErgaenzungenEinfuegen) return;
    setLaeuft('einfuegen');
    try {
      await onErgaenzungenEinfuegen();
    } finally {
      setLaeuft('');
    }
  }, [onErgaenzungenEinfuegen]);

  if (!dealId) return null;

  // Eingeklappt bleibt ein schmaler Streifen stehen - sonst faende man den
  // Chat nicht wieder.
  if (!offen) {
    return (
      <div className="verhandlung-spalte verhandlung-spalte--zu" style={{
        width: 44, flexShrink: 0, borderRight: `1px solid ${T.randWeich}`,
        background: T.grund, display: 'flex', flexDirection: 'column', alignItems: 'center',
        paddingTop: 12, gap: 9,
        // alignSelf bleibt beim flex-start des Containers: Sonst streckt sich
        // die Spalte auf die Hoehe der Vertragsvorschau - und die ist bei
        // einem mehrseitigen Vertrag mehrere Bildschirme hoch.
        height: '100%',
      }}>
        <button type="button" onClick={umschalten} title="Gespräch öffnen"
          style={{ ...knopfStil(), padding: '7px 5px' }}>
          <ChevronRight size={14} />
        </button>
        <MessageSquare size={15} color={T.muted} />
        {zaehler.passt > 0 ? (
          <span style={{
            fontSize: T.tLabel, fontWeight: 700, color: T.gut, background: T.gutGrund,
            border: `1px solid ${T.gutRand}`, borderRadius: 999, padding: '2px 6px',
          }}>{zaehler.passt}</span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="verhandlung-spalte" style={{
      width: SPALTE_BREIT, flexShrink: 0,
      borderRight: `1px solid ${T.randWeich}`, background: T.flaeche,
      display: 'flex', flexDirection: 'column',
      // height:100% statt sticky: Der Arbeitsbereich hat seit dem Umbau eine
      // echte Hoehe, also genuegt es, sie zu uebernehmen.
      height: '100%',
    }}>
      {/* Einzeilig. Die erste Fassung hatte Titel und eine Erklaerzeile
          darunter - also genau den Erklaerkasten, der einen Tag zuvor aus dem
          Studio-Kopf geflogen war, nur an anderer Stelle. Wer hier arbeitet,
          weiss, wofuer der Bereich da ist; die Erklaerung haengt jetzt am
          Titel und erscheint beim Darueberfahren. */}
      <div style={{
        padding: '9px 10px 9px 16px', borderBottom: `1px solid ${T.randWeich}`,
        background: T.flaeche, display: 'flex', alignItems: 'center', gap: 8,
        flexShrink: 0,
      }}>
        <MessageSquare size={14} color={T.navy} style={{ flexShrink: 0 }} />
        <span
          title="Besprochenes wird zu Vertragstext — Satz für Satz von dir freigegeben"
          style={{
            flex: 1, minWidth: 0, fontSize: T.tTitel, fontWeight: 700,
            color: T.navy, letterSpacing: T.spurTitel,
          }}
        >
          Aus dem Gespräch
        </span>
        {etwasDa ? (
          <button type="button" onClick={() => setNeuBeginnenFrage((v) => !v)} disabled={Boolean(laeuft)}
            title="Neu beginnen — das Gespräch leeren, auf Wunsch auch Ergänzungen und Ersetzungen"
            aria-label="Neu beginnen" aria-expanded={neuBeginnenFrage}
            style={{ ...knopfStil(neuBeginnenFrage), padding: '5px 7px', opacity: laeuft ? 0.45 : 1 }}>
            <RotateCcw size={14} />
          </button>
        ) : null}
        <button type="button" onClick={umschalten} title="Einklappen"
          style={{ ...knopfStil(), padding: '5px 7px' }}>
          <ChevronLeft size={14} />
        </button>
      </div>

      {/* Rueckfrage an Ort und Stelle, wie beim Aufraeumen. Zwei Stufen, weil
          "neu starten" beides heissen kann: nur den Verlauf los sein oder
          ganz von vorn. Rueckgaengig geht danach am Hinweis. */}
      {neuBeginnenFrage ? (
        <div style={{
          padding: '10px 16px', borderBottom: `1px solid ${T.warnRand}`, background: T.warnGrund,
          display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0,
        }}>
          <div style={{ fontSize: T.tMeta, color: T.ink, lineHeight: 1.55 }}>
            <strong style={{ color: T.warn }}>Neu beginnen?</strong>{' '}
            Die KI vergisst das bisherige Gespräch. Was schon im Vertrag steht, bleibt dort; das Abwägen bleibt unberührt.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => zuruecksetzen(false)} disabled={Boolean(laeuft)}
              title="Verlauf, Notizen und Gliederung leeren — Ergänzungen und Ersetzungen bleiben"
              style={knopfStil()}>
              Nur das Gespräch leeren
            </button>
            <button type="button" onClick={() => zuruecksetzen(true)} disabled={Boolean(laeuft)}
              title="Auch alle Ergänzungen und Ersetzungen entfernen — auch Freigegebenes"
              style={{ ...knopfStil(), borderColor: T.gefahrRand, color: T.gefahr }}>
              Alles zurücksetzen
            </button>
            <button type="button" onClick={() => setNeuBeginnenFrage(false)} style={textKnopfStil}>Abbrechen</button>
          </div>
          <div style={{ fontSize: T.tMikro, color: T.muted, lineHeight: 1.5 }}>
            „Alles“ entfernt auch Ergänzungen und Ersetzungen, auch Freigegebenes. Direkt danach lässt sich beides rückgängig machen.
          </div>
        </div>
      ) : null}

      {!kiBereit ? (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 16px',
          background: T.warnGrund, borderBottom: `1px solid ${T.warnRand}`,
          fontSize: T.tMeta, color: T.warn, flexShrink: 0,
        }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Kein Vibecode-Schlüssel hinterlegt — Entwerfen ist nicht möglich.</span>
        </div>
      ) : null}

      {/* Steht bewusst AUSSERHALB der Reiter-Bereiche: Sonst wäre das Feld
          nicht da, sobald ein anderer Reiter offen ist, und der Klick auf die
          Büroklammer liefe ins Leere. */}
      <input
        ref={dateiFeld} type="file" onChange={dateiWaehlen}
        accept={DATEI_ANNAHME}
        style={{ display: 'none' }}
      />

      <div style={{
        display: 'flex', borderBottom: `1px solid ${T.randWeich}`,
        background: T.grund, flexShrink: 0,
      }}>
        <button type="button" onClick={() => setReiter('gespraech')} style={reiterStil(reiter === 'gespraech')}>
          Gespräch
        </button>
        <button type="button" onClick={() => setReiter('passus')} style={reiterStil(reiter === 'passus')}>
          Ergänzungen{zaehler.gesamt > 0 ? ` (${zaehler.passt}/${zaehler.gesamt})` : ''}
        </button>
        <button type="button" onClick={() => setReiter('aenderungen')}
          style={{
            ...reiterStil(reiter === 'aenderungen'),
            // Nur ueberschreiben, wenn es etwas zu sagen gibt - "color:
            // undefined" loeschte sonst die Farbe aus reiterStil, und der leere
            // Reiter sah aus wie der aktive.
            ...(aenderungen.length > 0 && reiter !== 'aenderungen' ? { color: T.gefahr } : {}),
          }}>
          Ersetzungen{aenderungen.length > 0 ? ` (${aenderungen.length})` : ''}
        </button>
        {/* Erst da, wenn es etwas zu gliedern gibt - im Normalfall bleibt es
            bei drei Reitern. */}
        {gliederung || quelleDa || laeuft === 'gliedern' ? (
          <button type="button" onClick={() => setReiter('gliederung')} style={reiterStil(reiter === 'gliederung')}>
            Gliederung{gliederungsStand.gesamt > 0 ? ` (${gliederungsStand.gewaehlt}/${gliederungsStand.gesamt})` : ''}
          </button>
        ) : null}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}>
        {reiter === 'gespraech' ? (
          verlauf.length === 0 && !unterwegs && laeuft !== 'chat' && laeuft !== 'datei' ? (
            <Wegweiser schritte={GESPRAECH_WEGWEISER} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {verlauf.map((beitrag, i) => (
                <Blase key={i} vonMir={beitrag.rolle === 'mensch'} rueckfrage={Boolean(beitrag.rueckfrage)}>
                  {beitrag.text}
                </Blase>
              ))}
              {unterwegs ? <Blase vonMir>{unterwegs}</Blase> : null}
              {laeuft === 'chat' ? <KiDenkt schritte={GESPRAECH_SCHRITTE} /> : null}
              {laeuft === 'datei' ? <KiDenkt schritte={['Datei wird gelesen …']} /> : null}
              {!laeuft ? <RundenErgebnis ergebnis={letzteRunde} onZeigen={setReiter} /> : null}
              {fehlend.length > 0 && !laeuft ? (
                <FehlendHinweis fehlend={fehlend} kiBereit={kiBereit}
                  onNachreichen={fehlendeNachreichen} onSchliessen={() => setFehlend([])} />
              ) : null}
              {/* Solange das neueste lange Schreiben nicht gegliedert ist - auch
                  wenn danach noch Nachrichten kamen (bis 29.09.2026 verschwand
                  der Hinweis mit der zweiten Nachricht). Seit 09.10.2026 der
                  einzige Weg zur Gliederung - von selbst gliedert die Seite
                  nichts mehr. */}
              {gliedernEmpfohlen(verlauf, gliederung, ohneGliederung) && !laeuft && !unterwegs && kiBereit ? (
                <div style={{
                  padding: '10px 12px', borderRadius: T.rChip, background: T.infoGrund,
                  borderLeft: `3px solid ${T.info}`, display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                  <div style={{ fontSize: T.tMeta, color: T.ink, lineHeight: 1.55 }}>
                    <strong style={{ color: T.navy }}>Langes Schreiben — noch nicht gegliedert.</strong>{' '}
                    Die KI zerlegt es in Punkte, du hakst an, was ENPOWER übernimmt —
                    erst dann wird formuliert.
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <button type="button" onClick={gliedern} style={primaerKnopfStil}>
                      <ListChecks size={12} /> Erst gliedern
                    </button>
                    <button type="button" onClick={ohneGliederungWeiter} style={textKnopfStil}>
                      Ohne Gliederung weiter
                    </button>
                  </div>
                </div>
              ) : null}
              <div ref={verlaufEnde} />
            </div>
          )
        ) : null}

        {reiter === 'passus' ? (
          passusListe.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
              <div style={ruhigerText}>Noch keine Ergänzungen. Sie entstehen im Gespräch.</div>
              <button type="button" onClick={() => setReiter('gespraech')}
                style={{ ...textKnopfStil, fontSize: T.tMeta, color: T.navy }}>
                Zum Gespräch <ArrowRight size={12} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minHeight: 20 }}>
                <span title="Nur Freigegebenes wird in den Vertrag eingefügt." style={{
                  fontSize: T.tMeta, fontWeight: 600,
                  color: aktive.length > 0 && zaehler.offen === 0 ? T.gut : T.muted,
                }}>
                  {aktive.length === 0
                    ? 'Alles verworfen.'
                    : zaehler.offen > 0
                      ? `${zaehler.passt} von ${aktive.length} freigegeben`
                      : 'Alles freigegeben — kommt so in den Vertrag.'}
                </span>
                {doppeltZahl > 0 ? (
                  <span title="Diese Ergänzungen stehen so oder fast so schon im Vertrag — eingefügt stünden sie doppelt."
                    style={{ fontSize: T.tMeta, fontWeight: 600, color: T.gefahr }}>
                    {doppeltZahl} schon im Vertrag
                  </span>
                ) : null}
                <span style={{ flex: 1 }} />
                <Aufraeumen anzahl={aufraeumbar} gesperrt={Boolean(laeuft)} onEntfernen={aufraeumen} />
              </div>

              {aktive.map((passus, i) => (
                <ErgaenzungKarte
                  key={passus.id}
                  passus={passus}
                  nummer={i + 1}
                  parteien={parteien}
                  gesperrt={Boolean(laeuft)}
                  beschaeftigt={laeuft === `passus-${passus.id}`}
                  kiBereit={kiBereit}
                  istErste={i === 0}
                  istLetzte={i === aktive.length - 1}
                  imVertrag={imVertragNach.get(passus.id) || null}
                  onStand={(stand, hinweis) => setzePassusStand(passus.id, stand, hinweis)}
                  onUeberarbeiten={(hinweis) => ueberarbeiten(passus.id, hinweis)}
                  onAngleichen={() => angleichen(passus.id)}
                  onZuErsetzung={() => zuErsetzung(passus.id)}
                  onVerschieben={(richtung) => verschiebe(passus.id, richtung)}
                  onSpringe={onSpringeZuStelle
                    ? () => springeZuErgaenzung(passus)
                    : null}
                  onLueckeFuellen={(luecke, wert) => fuelleLuecke('passus', passus.id, luecke, wert)}
                />
              ))}

              {verworfene.length > 0 ? (
                <div style={{ marginTop: 2 }}>
                  <Aufklapper offen={verworfenOffen} onClick={() => setVerworfenOffen((v) => !v)}>
                    Verworfen ({verworfene.length})
                  </Aufklapper>
                  {verworfenOffen ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                      {verworfene.map((passus) => (
                        <VerworfeneKarte
                          key={passus.id}
                          passus={passus}
                          gesperrt={Boolean(laeuft)}
                          onStand={(stand) => setzePassusStand(passus.id, stand)}
                          onLoeschen={() => loeschePassus(passus.id)}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          )
        ) : null}

        {reiter === 'aenderungen' ? (
          aenderungen.length === 0 ? (
            <div style={ruhigerText}>
              Keine Ersetzungen — das ist der Normalfall. Die KI schlägt eine nur vor, wenn sonst
              zwei widersprüchliche Regelungen im Vertrag stünden.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <ErsetzungenHinweis />
              {aenderungen.map((aenderung, i) => (
                <ErsetzungKarte
                  key={aenderung.id}
                  aenderung={aenderung}
                  nummer={i + 1}
                  zugehoerig={aenderung.gehoertZu ? (ergaenzungNach.get(aenderung.gehoertZu) || null) : null}
                  ort={ersetzungsOrtNach.get(aenderung.id) || null}
                  onLueckeFuellen={(luecke, wert) => fuelleLuecke('aenderung', aenderung.id, luecke, wert)}
                  gesperrt={Boolean(laeuft)}
                  onStand={(stand) => setzeAenderungStand(aenderung.id, stand)}
                  onSpringe={onSpringeZuStelle ? () => springeZuErsetzung(aenderung) : null}
                />
              ))}
            </div>
          )
        ) : null}

        {reiter === 'gliederung' ? (
          laeuft === 'gliedern' ? (
            <KiDenkt schritte={GLIEDERUNG_SCHRITTE} />
          ) : gliederung ? (
            <GliederungsListe
              gliederung={gliederung}
              gesperrt={gliederungGesperrt}
              kiBereit={kiBereit}
              imVertragNach={gliederungImVertrag}
              onWahl={(id, gewaehlt) => setzeGliederungsPunkte([{ id, gewaehlt }])}
              onAlle={alleWaehlen}
              onNotiz={(id, notiz) => setzeGliederungsPunkte([{ id, notiz }])}
              onNochmal={(id) => setzeGliederungsPunkte([{ id, entworfen: false, gewaehlt: true }])}
              onNeuGliedern={gliedern}
              onVerwerfen={gliederungVerwerfen}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
              <div style={ruhigerText}>
                Ein langes Schreiben — etwa die Anmerkungen eines Anwalts — erst in Punkte zerlegen,
                dann entscheiden, was ENPOWER übernimmt. Formuliert wird nur das Angehakte.
              </div>
              {quelleDa ? (
                <button type="button" onClick={gliedern} disabled={Boolean(laeuft) || !kiBereit}
                  style={{ ...primaerKnopfStil, opacity: (laeuft || !kiBereit) ? 0.45 : 1 }}>
                  <ListChecks size={12} /> Schreiben gliedern
                </button>
              ) : (
                <div style={ruhigerText}>Hänge es dazu im Gespräch als Datei an oder füge es ein.</div>
              )}
            </div>
          )
        ) : null}
      </div>

      {/* Entworfen wird nur Angehaktes, fuenf Punkte je Runde. */}
      {reiter === 'gliederung' && gliederung && (gliederungsStand.offen > 0 || entwurfsLauf) ? (
        <div style={{
          borderTop: `1px solid ${T.randWeich}`, padding: '10px 16px', background: T.flaeche,
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, flexShrink: 0,
        }}>
          {entwurfsLauf ? (
            <>
              <span style={{ flex: 1, fontSize: T.tMeta, color: T.body }}>{gliederungFortschritt(entwurfsLauf)}</span>
              <button type="button" onClick={() => { anhalten.current = true; toast.info('Hält nach dieser Runde an.'); }}
                style={knopfStil()}>
                Anhalten
              </button>
            </>
          ) : (
            <button type="button" onClick={entwerfeGewaehlte} disabled={Boolean(laeuft) || !kiBereit}
              title="Die angehakten Punkte als Ergänzungen und Ersetzungen formulieren — fünf je Runde"
              style={{ ...primaerKnopfStil, opacity: (laeuft || !kiBereit) ? 0.45 : 1 }}>
              <Sparkles size={12} /> {entwerfenKnopfText(gliederungsStand.offen)}
            </button>
          )}
        </div>
      ) : null}

      {/* Nur beim Bearbeiten: Dort gibt es kein "Fuellen", das die
          freigegebenen Ergaenzungen in den Vertrag bringt. Doppelt einfuegen
          geht nicht - der Server erkennt einen schon eingefuegten Passus an
          seiner Kennung. */}
      {reiter === 'passus' && zaehler.passt > 0 && onErgaenzungenEinfuegen ? (
        <div style={{
          borderTop: `1px solid ${T.randWeich}`, padding: '10px 16px', background: T.flaeche,
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap', flexShrink: 0,
        }}>
          {doppeltFreigegeben > 0 ? (
            <span style={{ flex: '1 1 180px', fontSize: T.tMikro, color: T.gefahr, lineHeight: 1.5 }}>
              {doppeltFreigegeben === 1 ? 'Eine davon steht' : `${doppeltFreigegeben} davon stehen`} schon im
              Vertrag — eingefügt stünde es doppelt. Bitte vorher prüfen.
            </span>
          ) : null}
          <button type="button" onClick={ergaenzungenEinfuegen} disabled={Boolean(laeuft)}
            title="Die freigegebenen Ergänzungen in den bearbeiteten Vertrag einfügen"
            style={{ ...primaerKnopfStil, opacity: laeuft && laeuft !== 'einfuegen' ? 0.45 : 1 }}>
            {laeuft === 'einfuegen'
              ? 'Wird eingefügt …'
              : `${zaehler.passt === 1 ? 'Freigegebene Ergänzung' : `${zaehler.passt} freigegebene Ergänzungen`} in den Vertrag einfügen`}
          </button>
        </div>
      ) : null}

      {/* Erscheint erst, wenn einer Ersetzung zugestimmt ist - dann ist das
          Uebernehmen der naechste Schritt. */}
      {reiter === 'aenderungen' && zugestimmt.length > 0 && onVertragGeaendert ? (
        <div style={{
          borderTop: `1px solid ${T.randWeich}`, padding: '10px 16px', background: T.flaeche,
          display: 'flex', justifyContent: 'flex-end', flexShrink: 0,
        }}>
          <button type="button" onClick={uebernehmen} disabled={Boolean(laeuft)}
            style={{
              ...primaerKnopfStil, background: T.gefahr, borderColor: T.gefahr,
              opacity: laeuft && laeuft !== 'anwenden' ? 0.45 : 1,
            }}>
            {laeuft === 'anwenden' ? 'Wird übernommen …' : `${ersetzungenWort(zugestimmt.length)} in den Vertrag übernehmen`}
          </button>
        </div>
      ) : null}

      {/* Beim Erstellen gibt es keinen Knopf: Zugestimmtes kommt mit Füllen und
          Abschließen in beide Varianten (seit 24.10.2026). Vorher setzte
          „übernehmen“ es nur in die Vorlage der aktiven Variante - die Vorschau
          blieb unverändert, und die Druckversion ging ohne die Ersetzung raus. */}
      {reiter === 'aenderungen' && zugestimmt.length > 0 && !onVertragGeaendert && erstellen ? (
        <div style={{
          borderTop: `1px solid ${T.randWeich}`, padding: '10px 16px', background: T.grund,
          fontSize: T.tMeta, color: T.body, lineHeight: 1.55, flexShrink: 0,
        }}>
          {zugestimmt.length === 1
            ? '1 Ersetzung zugestimmt — sie kommt'
            : `${zugestimmt.length} Ersetzungen zugestimmt — sie kommen`}
          {' '}beim Füllen und Abschließen in den Vertrag, in beide Varianten.
        </div>
      ) : null}

      {/* Eingabe - nur im Gespräch, sonst nähme sie nur Platz weg */}
      {reiter === 'gespraech' ? (
        <div style={{
          borderTop: `1px solid ${T.randWeich}`, padding: '12px 14px', background: T.grund, flexShrink: 0,
        }}>
          <div style={{ display: 'flex', gap: 7, alignItems: 'flex-end' }}>
            <button
              type="button" onClick={() => dateiFeld.current?.click()}
              disabled={Boolean(laeuft)}
              title="Datei anhängen — PDF, Bild, Text oder Mail. Bilder schreibt die KI ab; gespeichert wird nur der Text."
              aria-label="Datei anhängen"
              style={{ ...knopfStil(), padding: '8px 9px', flexShrink: 0, opacity: laeuft ? 0.45 : 1 }}
            >
              {laeuft === 'datei' ? <FileUp size={14} /> : <Paperclip size={14} />}
            </button>

            <textarea
              value={nachricht}
              onChange={(e) => setNachricht(e.target.value)}
              onKeyDown={(e) => {
                // Enter schickt, Umschalt+Enter macht einen Absatz - wie im Chat
                // gewohnt. Ohne das tippt man aus Versehen Absätze statt zu senden.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!laeuft && kiBereit) schicken();
                }
              }}
              aria-label="Nachricht an die KI"
              placeholder={verlauf.length === 0 && !unterwegs
                ? 'Was wurde besprochen? Stichpunkte genügen.'
                : 'Antworten oder nachlegen …'}
              rows={3}
              disabled={!kiBereit}
              style={{ ...feldStil, resize: 'vertical', opacity: kiBereit ? 1 : 0.5 }}
            />

            <button
              type="button" onClick={schicken}
              disabled={Boolean(laeuft) || !kiBereit || !nachricht.trim()}
              title="Senden (Enter)"
              aria-label="Senden"
              style={{
                ...primaerKnopfStil, padding: '9px 11px', flexShrink: 0,
                opacity: (laeuft || !kiBereit || !nachricht.trim()) ? 0.4 : 1,
              }}
            ><Send size={14} /></button>
          </div>

          {/* Fuer den Fall, dass nur Dateien im Gespraech liegen oder man
              alles noch einmal zusammenfassen lassen will - jede Nachricht
              entwirft ohnehin schon mit. */}
          {verlauf.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <button
                type="button" onClick={entwerfen}
                disabled={Boolean(laeuft) || !kiBereit}
                title="Aus dem ganzen bisherigen Gespräch alles Regelungsbedürftige als Ergänzungen formulieren"
                style={{ ...textKnopfStil, color: T.navy, opacity: (laeuft || !kiBereit) ? 0.45 : 1 }}
              ><Sparkles size={12} /> Ergänzungen aus dem Gespräch entwerfen</button>
              {quelleDa ? (
                <button
                  type="button" onClick={gliederung ? () => setReiter('gliederung') : gliedern}
                  disabled={Boolean(laeuft) || !kiBereit}
                  title="Ein langes Schreiben erst in Punkte zerlegen und ankreuzen, was übernommen wird"
                  style={{ ...textKnopfStil, color: T.navy, marginLeft: 14, opacity: (laeuft || !kiBereit) ? 0.45 : 1 }}
                ><ListChecks size={12} /> {gliederung ? 'Zur Gliederung' : 'Schreiben gliedern'}</button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
